import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Person as UserIcon,
  HourglassEmpty as PendingIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface LeaveRequest {
  _id: string;
  personnelId: {
    _id: string;
    name: string;
    email: string;
  };
  requestedBy: {
    _id: string;
    name: string;
    email: string;
  };
  type: 'annual-leave' | 'time-lieu';
  dateStart: string;
  dateEnd?: string;
  hoursRequested?: number;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  decisionBy?: {
    _id: string;
    name: string;
    email: string;
  };
  decisionDate?: string;
  createdAt: string;
  updatedAt?: string;
}

const LeaveRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  // Rejection modal state
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/leave-requests');
      console.log('📋 Leave requests fetched:', res.data);
      setRequests(res.data);
    } catch (error: any) {
      console.error('Error fetching leave requests:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch leave requests',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      setActionLoading(true);
      await axiosInstance.patch(`/api/leave-requests/${id}/approve`);
      
      setSnackbar({
        open: true,
        message: 'Request approved successfully! Staff member has been notified.',
        severity: 'success',
      });
      
      // Refresh the list
      await fetchRequests();
      
    } catch (error: any) {
      console.error('Error approving request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to approve request',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectionModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedRequest) return;
    
    if (!rejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a reason for rejection',
        severity: 'warning',
      });
      return;
    }

    try {
      setActionLoading(true);
      await axiosInstance.patch(`/api/leave-requests/${selectedRequest._id}/reject`, {
        decisionNote: rejectionReason
      });
      
      setSnackbar({
        open: true,
        message: 'Request rejected. Staff member has been notified.',
        severity: 'success',
      });
      
      // Close modal and refresh
      setRejectionModalOpen(false);
      setSelectedRequest(null);
      setRejectionReason('');
      await fetchRequests();
      
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reject request',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'approved':
        return <Chip icon={<CheckIcon />} label="Approved" color="success" size="small" />;
      case 'rejected':
        return <Chip icon={<CancelIcon />} label="Rejected" color="error" size="small" />;
      default:
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-IE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleString('en-IE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Calculate days of notice given
  const getDaysNotice = (createdAt: string, dateStart: string) => {
    try {
      const requestDate = new Date(createdAt);
      const leaveDate = new Date(dateStart);
      
      if (isNaN(requestDate.getTime()) || isNaN(leaveDate.getTime())) {
        return null;
      }
      
      const diffTime = leaveDate.getTime() - requestDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (e) {
      return null;
    }
  };

  const getNoticeChip = (daysNotice: number | null) => {
    if (daysNotice === null) return null;
    
    if (daysNotice < 7) {
      return (
        <Tooltip title="Less than 1 week notice">
          <Chip 
            icon={<WarningIcon />} 
            label={`${daysNotice}d notice`} 
            color="error" 
            size="small" 
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    } else if (daysNotice < 14) {
      return (
        <Tooltip title="Less than 2 weeks notice">
          <Chip 
            icon={<WarningIcon />} 
            label={`${daysNotice}d notice`} 
            color="warning" 
            size="small" 
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Sufficient notice given">
          <Chip 
            label={`${daysNotice}d notice`} 
            color="success" 
            size="small" 
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }
  };

  return (
    <Box sx={{ p: 3, color: 'text.primary' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
        Leave Requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and manage staff leave requests. Approve or reject them directly here.
        <br />
        <strong>Notice Policy:</strong> Staff should provide at least 2 weeks notice for leave requests.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Staff</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Type</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Leave Date</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Requested On</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Hours</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Reason</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Status</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No leave requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map(request => {
                    const daysNotice = getDaysNotice(request.createdAt, request.dateStart);
                    
                    return (
                      <TableRow key={request._id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <UserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {request.personnelId?.name || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.personnelId?.email || 'No email'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {request.type === 'annual-leave' ? 'Annual Leave' : 'Time in Lieu'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {formatDate(request.dateStart)}
                              </Typography>
                              {getNoticeChip(daysNotice)}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {formatDateTime(request.createdAt)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {request.hoursRequested ? `${request.hoursRequested}h` : '-'}
                        </TableCell>

                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {request.reason || '-'}
                        </TableCell>

                        <TableCell>{getStatusChip(request.status)}</TableCell>

                        <TableCell align="right">
                          {request.status === 'pending' ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="contained"
                                disabled={actionLoading}
                                sx={{
                                  bgcolor: '#3A9188',
                                  '&:hover': { bgcolor: '#2D7A72' },
                                }}
                                onClick={() => handleApprove(request._id)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={actionLoading}
                                onClick={() => handleRejectClick(request)}
                              >
                                Reject
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {request.status === 'approved'
                                ? `Approved by ${request.decisionBy?.name || 'Admin'}`
                                : 'Rejected'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Rejection Modal */}
      <Dialog 
        open={rejectionModalOpen} 
        onClose={() => !actionLoading && setRejectionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon />
            <Typography variant="h6">Reject Leave Request</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {selectedRequest && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You are about to reject the leave request from <strong>{selectedRequest.personnelId?.name}</strong> for{' '}
                <strong>{formatDate(selectedRequest.dateStart)}</strong>.
              </Typography>

              <TextField
                label="Reason for Rejection"
                multiline
                rows={4}
                fullWidth
                required
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Insufficient coverage on this date, please choose an alternative day..."
                helperText="This reason will be sent to the staff member via email."
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setRejectionModalOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectionReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default LeaveRequestsPage;